import React, { useCallback, useEffect, useRef, useState } from "react";
import API from "../utils/api";

const PING_INTERVAL_MS = 5 * 60 * 1000;

function isTrackingTime() {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return now.getDay() !== 0 && now.getHours() >= 9 && now.getHours() < 18;
}

const keyFor = (userId) => `crm_location_queue_${userId || "self"}`;
function readQueue(userId) {
  try { return JSON.parse(localStorage.getItem(keyFor(userId)) || "[]"); }
  catch { return []; }
}
function writeQueue(userId, rows) {
  localStorage.setItem(keyFor(userId), JSON.stringify(rows.slice(-200)));
  window.dispatchEvent(new Event("crm-location-queue-changed"));
}
function setIssue(message = "") {
  if (message) localStorage.setItem("crm_location_tracking_issue", message);
  else localStorage.removeItem("crm_location_tracking_issue");
  window.dispatchEvent(new Event("crm-location-queue-changed"));
}
export default function LocationTracker({ user }) {
  const [checkin, setCheckin] = useState(null);
  const flushing = useRef(false);
  const lastPingTime = useRef(0);

  const refreshAttendance = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await API.get(`/checkins/today?_=${Date.now()}`, {
        headers: { "Cache-Control": "no-cache" },
      });
      setCheckin(response.data?.checkin || null);
    } catch { /* retain last known state during a transient failure */ }
  }, [user?.id]);

  const sendSample = useCallback(async (payload) => {
    const response = await API.post("/location-logs", payload);
    if (!response.data?.ok || response.data?.skipped) throw new Error("Location sample was not accepted");
  }, []);

  const flushLocations = useCallback(async () => {
    if (!user?.id || !navigator.onLine || flushing.current) return;
    flushing.current = true;
    try {
      const remaining = [];
      for (const payload of readQueue(user.id)) {
        try { await sendSample(payload); }
        catch { remaining.push(payload); }
      }
      writeQueue(user.id, remaining);
      if (!remaining.length) setIssue("");
    } finally { flushing.current = false; }
  }, [sendSample, user?.id]);

  useEffect(() => {
    refreshAttendance();
    const visible = () => { if (document.visibilityState === "visible") refreshAttendance(); };
    window.addEventListener("focus", refreshAttendance);
    window.addEventListener("online", refreshAttendance);
    window.addEventListener("sales-attendance-marked", refreshAttendance);
    document.addEventListener("visibilitychange", visible);
    const timer = window.setInterval(refreshAttendance, 60 * 1000);
    return () => {
      window.removeEventListener("focus", refreshAttendance);
      window.removeEventListener("online", refreshAttendance);
      window.removeEventListener("sales-attendance-marked", refreshAttendance);
      document.removeEventListener("visibilitychange", visible);
      window.clearInterval(timer);
    };
  }, [refreshAttendance]);

  useEffect(() => {
    const visible = () => { if (document.visibilityState === "visible") flushLocations(); };
    window.addEventListener("online", flushLocations);
    window.addEventListener("crm-sync-request", flushLocations);
    document.addEventListener("visibilitychange", visible);
    const timer = window.setInterval(flushLocations, 60 * 1000);
    flushLocations();
    return () => {
      window.removeEventListener("online", flushLocations);
      window.removeEventListener("crm-sync-request", flushLocations);
      document.removeEventListener("visibilitychange", visible);
      window.clearInterval(timer);
    };
  }, [flushLocations]);

  useEffect(() => {
    if (!user?.id || !checkin || checkin.check_out_time || checkin.plan_type === "planned_leave") return undefined;
    if (!navigator.geolocation) return undefined;
    if (!isTrackingTime()) return undefined;
    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        if (!isTrackingTime()) {
          navigator.geolocation.clearWatch(watchId);
          return;
        }
        const now = Date.now();
        if (now - lastPingTime.current < PING_INTERVAL_MS) return;
        lastPingTime.current = now;
        const payload = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy || null,
          speed_mps: position.coords.speed || null,
          heading: position.coords.heading || null,
          recorded_at: new Date(position.timestamp || now).toISOString(),
        };
        try { await sendSample(payload); setIssue(""); }
        catch {
          writeQueue(user.id, [...readQueue(user.id), payload]);
          setIssue("Location updates are waiting to sync. Keep CRM open and tap Retry now in CRM Sync Centre.");
        }
      },
      (error) => setIssue(error?.code === 1
        ? "Location permission is blocked. Enable precise location for CRM field tracking."
        : "CRM cannot read the current location. Turn on GPS and keep the app open."),
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 20000 }
    );
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const sixPm = new Date(now);
    sixPm.setHours(18, 0, 0, 0);
    const stopTimer = setTimeout(() => navigator.geolocation.clearWatch(watchId), Math.max(0, sixPm - now));
    return () => {
      clearTimeout(stopTimer);
      navigator.geolocation.clearWatch(watchId);
    };
  }, [checkin, sendSample, user?.id]);

  return null;
}
